/**
 * Network > People > one person.
 *
 * Answers "who is this, and why can or can't they get in". One row per office
 * they hold a tie to, carrying BOTH facts side by side: membership is the
 * prerequisite, access is the permission, and every problem state is a
 * combination of the two (a member of a managed partner with no grant signs in
 * and 403s, which no other surface shows).
 *
 * A granted head office already covers every location under it, so those
 * locations are not listed - the head office row reports what it covers instead.
 *
 * Composition is deliberately flat: ONE table carries the substance, and the
 * supporting facts sit on hairline meta strips rather than in cards. The card
 * version of this page spent four boxes restating the heading (first name, last
 * name, email) and two more on a single "0", which is the opposite of what the
 * design system is for.
 *
 * ``User`` is a Tier 3 (reference-only) entity, so this page is read-mostly by
 * design: allow-listed fields, no custom attributes, never an Entity Editor
 * surface. Anything editable belongs to the partnership or the membership.
 */

import { useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2, UserCircle } from 'lucide-react'

import { AppShell } from '@/admin/components/app-shell'
import {
  DataTable,
  type DataTableColumn,
} from '@/admin/components/data-table'
import {
  OfficeName,
  OfficeTierBadge,
} from '@/admin/components/shared/partner-office'
import {
  ACCESS_SEVERITY,
  ACCESS_TONE,
  useAccessLabels,
} from '@/admin/components/shared/partner-office-access'
import { EmptyState } from '@/admin/components/shared/empty-state'
import {
  useNetworkPerson,
  useRemovePersonFromOffice,
  type PartnerOffice,
} from '@/admin/hooks/use-network-people'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { ConfirmationDialog } from '@/admin/components/shared/confirmation-dialog'
import { Button } from '@/shared/components/ui/button'
import { useFormatters } from '@/shared/lib/formatting'
import { cn } from '@/shared/lib/utils'

/**
 * Summary band - the same contained label/value grid the partnership detail page
 * uses. Bare hairline strips read as unanchored on a short page; a bordered band
 * gives the facts a home without spending a card on each one.
 */
function SummaryBand({
  items,
}: {
  items: { key: string; label: string; value: ReactNode; muted?: boolean }[]
}) {
  return (
    <dl className="m-0 grid grid-cols-2 gap-6 rounded-[8px] border-[0.5px] border-[var(--rule)] p-5 md:grid-cols-4">
      {items.map((item) => (
        <div key={item.key}>
          <dt className="clara-label mb-1">{item.label}</dt>
          <dd
            className={cn(
              'm-0 text-[13px]',
              item.muted ? 'text-[var(--ink-3)]' : 'text-[var(--ink)]',
            )}
          >
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  )
}

export function PartnerPersonPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { uuid } = useParams<{ uuid: string }>()
  const { formatDate, formatDateTime } = useFormatters()

  const { data: person, isPending, isError } = useNetworkPerson(uuid)

  const { can } = usePermissions()
  const canManageUsers = can('users.manage')
  const removeFromOffice = useRemovePersonFromOffice()
  const [officeToRemove, setOfficeToRemove] = useState<PartnerOffice | null>(
    null,
  )
  const [removeError, setRemoveError] = useState<string | null>(null)

  const { accessLabel } = useAccessLabels()

  /**
   * Open a partner record, carrying the origin so its Back returns HERE rather
   * than to the partner list the admin never visited.
   */
  const openPartner = (partnershipUuid: string) =>
    navigate(`/network/${partnershipUuid}`, {
      state: {
        backTo: `/network/people/${uuid}`,
        backLabel: t('network.person.backToPerson', 'Back to person'),
      },
    })

  const backLink = (
    <Button
      variant="ghost"
      onClick={() => navigate('/network/people')}
      className="mb-5 inline-flex h-auto items-center gap-1.5 px-0 text-sm uppercase tracking-[0.1em] text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
    >
      <ArrowLeft className="h-3 w-3" />
      {t('network.person.back', 'People')}
    </Button>
  )

  if (isPending) {
    return (
      <AppShell>
        <main className="page-container py-10">
          {backLink}
          <div className="flex justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
          </div>
        </main>
      </AppShell>
    )
  }

  if (isError || !person) {
    return (
      <AppShell>
        <main className="page-container py-10">
          {backLink}
          <EmptyState
            icon={UserCircle}
            title={t('network.person.notFound', 'Person not found')}
            description={t(
              'network.person.notFoundDescription',
              'They may no longer reach your network through any partner.',
            )}
          />
        </main>
      </AppShell>
    )
  }

  const strandedOffices = person.offices.filter(
    (office) => office.access === 'no_grant',
  )
  // The header band already states this person's overall access, so repeating
  // it per office only earns its column when the offices DISAGREE. Same for
  // partner role, which is one value on most tenants.
  const varies = (pick: (office: PartnerOffice) => string) =>
    new Set(person.offices.map(pick)).size > 1
  const showAccess = varies((office) => accessLabel(office.access))
  const showRole = varies((office) => office.role_name ?? '-')

  // Style-guide: tables render through DataTable, never hand-rolled <table>.
  // The conditional Role / Access columns are filtered out rather than
  // rendered empty, so a person whose offices all agree sees no dead column.
  const officeColumns: DataTableColumn<PartnerOffice>[] = [
      {
        id: 'office',
        header: t('network.person.office', 'Office'),
        cell: (office: PartnerOffice) => (
          <span className="flex min-w-0 items-center gap-2">
            <OfficeName office={office} onOpen={openPartner} />
            <OfficeTierBadge office={office} />
            {office.covered_location_count > 0 && (
              <span className="text-[10px] tracking-[0.04em] text-[var(--ink-3)]">
                {office.covered_location_count === 1
                  ? t('network.people.coversLocation', 'covers 1 location')
                  : t(
                      'network.people.coversLocations',
                      'covers {{count}} locations',
                      { count: office.covered_location_count },
                    )}
              </span>
            )}
          </span>
        ),
      },
      {
        id: 'member_since',
        header: t('network.person.onStaffSince', 'On staff since'),
        cell: (office: PartnerOffice) => (
          // Missing membership is a staff-record problem, so it is flagged
          // here - not by claiming in the access column that they cannot
          // get in.
          <span
            className={cn(
              'whitespace-nowrap',
              office.member_since
                ? 'text-[var(--ink-2)]'
                : 'text-[var(--status-warn)]',
            )}
          >
            {office.member_since
              ? formatDate(office.member_since)
              : t('network.person.notAMember', 'Not on staff')}
          </span>
        ),
      },
      showRole
        ? {
            id: 'role',
            header: t('network.people.colRole', 'Partner role'),
            cell: (office: PartnerOffice) => (
              <span className="text-[var(--ink-2)]">
                {office.role_name ?? '-'}
              </span>
            ),
          }
        : null,
      showAccess
        ? {
            id: 'access',
            header: t('network.people.colAccess', 'Access'),
            cell: (office: PartnerOffice) => (
              <span
                className={cn(
                  'inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[9px] uppercase tracking-[0.1em]',
                  ACCESS_TONE[office.access],
                )}
              >
                {accessLabel(office.access)}
              </span>
            ),
          }
        : null,
      {
        id: 'granted_at',
        header: t('network.person.granted', 'Access since'),
        cell: (office: PartnerOffice) => (
          <span className="whitespace-nowrap text-[var(--ink-2)]">
            {office.granted_at ? formatDate(office.granted_at) : '-'}
          </span>
        ),
      },
      {
        id: 'action',
        header: t('network.person.hdrAction', 'Action'),
        // Removal is per OFFICE, not per person: the common case is moving
        // someone off one site and leaving the rest. Only offered where
        // there is a membership to remove - a grant-only tie has no row to
        // soft-delete.
        cell: (office: PartnerOffice) =>
          canManageUsers && office.membership_uuid ? (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              className="text-[var(--status-error)] hover:bg-[var(--status-error-bg)]"
              onClick={() => setOfficeToRemove(office)}
            >
              {t('network.person.removeFromOffice', 'Remove')}
            </Button>
          ) : null,
      },
  ].filter(Boolean) as DataTableColumn<PartnerOffice>[]

  // Worst state across their offices - the band should answer "is there a
  // problem" before it reports dates.
  const invitation = person.invitation
  // When there is no acceptance date, say what happened instead of leaving a dash.
  const inviteOutcome = invitation
    ? {
        accepted: t('network.person.inviteAccepted', 'Accepted'),
        pending: t('network.person.invitePending', 'Not yet'),
        cancelled: t('network.person.inviteCancelled', 'Cancelled'),
        expired: t('network.person.inviteExpired', 'Expired'),
      }[invitation.status]
    : '-'
  const worstAccess = [...person.offices].sort(
    (a, b) => ACCESS_SEVERITY[b.access] - ACCESS_SEVERITY[a.access],
  )[0]?.access

  return (
    <AppShell>
      <main className="page-container py-10">
        {backLink}

        {/* Identity. The name IS the first name and last name, and the email is
            right beneath it - so neither is repeated in a card below. */}
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="clara-heading">{person.full_name || person.email}</h1>
            <p className="clara-small mt-1">{person.email}</p>
          </div>
          <span className="mt-1 flex flex-wrap items-center gap-2">
            <span
              className={cn(
                'inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[9px] uppercase tracking-[0.1em]',
                person.is_active
                  ? 'bg-[var(--status-ok-bg)] text-[var(--status-ok)]'
                  : 'bg-[var(--bg-subtle)] text-[var(--ink-3)]',
              )}
            >
              {person.is_active
                ? t('network.person.active', 'Active')
                : t('network.person.inactive', 'Inactive')}
            </span>
            {worstAccess && (
              <span
                className={cn(
                  'inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[9px] uppercase tracking-[0.1em]',
                  ACCESS_TONE[worstAccess],
                )}
              >
                {accessLabel(worstAccess)}
              </span>
            )}
          </span>
        </div>

        <SummaryBand
          items={[
            {
              key: 'invited',
              label: t('network.person.invited', 'Invited'),
              value: invitation?.sent_at
                ? formatDate(invitation.sent_at)
                : invitation
                  ? formatDate(invitation.created_at)
                  : t('network.person.invitationNever', 'Never invited'),
              muted: !invitation,
            },
            {
              key: 'accepted',
              label: t('network.person.accepted', 'Accepted'),
              value: invitation?.accepted_at
                ? formatDate(invitation.accepted_at)
                : invitation
                  ? inviteOutcome
                  : '-',
              muted: !invitation?.accepted_at,
            },
            {
              key: 'signed-in',
              label: t('network.person.lastSignedIn', 'Last signed in'),
              value: person.last_login
                ? formatDateTime(person.last_login)
                : t('network.people.never', 'Never'),
              muted: !person.last_login,
            },
            {
              key: 'added',
              label: t('network.person.added', 'Account added'),
              value: person.date_joined ? formatDate(person.date_joined) : '-',
              muted: !person.date_joined,
            },
          ]}
        />

        {/* Partner access - the substance of the page. */}
        <section className="mt-8">
          <p className="clara-section-label mb-3">
            {t('network.person.accessSection', 'Partner access')}
          </p>

          {person.offices.length === 0 ? (
            <EmptyState
              icon={UserCircle}
              title={t('network.person.noOffices', 'No partner offices')}
              description={t(
                'network.person.noOfficesDescription',
                'They hold no membership or access anywhere in your network.',
              )}
            />
          ) : (
            <DataTable<PartnerOffice>
              tableId="partner-person-offices"
              columns={officeColumns}
              data={person.offices}
              rowKey={(office) => office.partnership_uuid}
            />
          )}

          <ConfirmationDialog
            open={officeToRemove !== null}
            onOpenChange={(next) => {
              if (!next) {
                setOfficeToRemove(null)
                setRemoveError(null)
              }
            }}
            destructive
            isPending={removeFromOffice.isPending}
            title={t('network.person.removeTitle', 'Remove from this office?')}
            confirmLabel={t('network.person.removeConfirm', 'Remove')}
            pendingLabel={t('network.person.removePending', 'Removing...')}
            description={
              <>
                {t(
                  'network.person.removeBody',
                  'Takes {{name}} off the staff list at {{office}} and revokes their access there. Their other offices are unaffected, and they can be added back later.',
                  {
                    name: person.full_name || person.email,
                    office: officeToRemove?.name ?? '',
                  },
                )}
                {removeError && (
                  <span className="mt-2 block text-[var(--status-error)]">
                    {removeError}
                  </span>
                )}
              </>
            }
            onConfirm={async () => {
              if (!officeToRemove?.membership_uuid) return
              setRemoveError(null)
              try {
                await removeFromOffice.mutateAsync({
                  organisationUuid: officeToRemove.organisation_uuid,
                  membershipUuid: officeToRemove.membership_uuid,
                })
                setOfficeToRemove(null)
              } catch (error) {
                // The server refuses the owner and self-removal; show its own
                // wording rather than guessing which rule was hit.
                const message = (
                  error as { response?: { data?: { message?: string } } }
                )?.response?.data?.message
                setRemoveError(
                  message ??
                    t(
                      'network.person.removeFailed',
                      'Could not remove them from this office. Please try again.',
                    ),
                )
              }
            }}
          />

          {/* The actionable case, in plain English rather than a badge to decode. */}
          {strandedOffices.length > 0 && (
            <p className="mt-4 max-w-[70ch] rounded-[6px] border-[0.5px] border-[var(--status-error)] bg-[var(--status-error-bg)] px-3.5 py-3 text-[13px] leading-relaxed text-[var(--status-error)]">
              {t(
                'network.person.strandedWarning',
                'On the staff list at {{offices}} but with no access there, so they cannot open it in the portal. For these partners access has to be given explicitly - being on the staff list is not enough.',
                { offices: strandedOffices.map((office) => office.name).join(', ') },
              )}
            </p>
          )}
        </section>

        {/* What they have actually done. Three counts, not three cards. */}
        <section className="mt-8">
          <p className="clara-section-label mb-3">
            {t('network.person.activityCard', 'Activity')}
          </p>
          <SummaryBand
            items={[
              {
                key: 'registrations',
                label: t('network.person.registrations', 'Registrations created'),
                value: person.activity.registrations,
                muted: person.activity.registrations === 0,
              },
              {
                key: 'claims',
                label: t('network.person.claims', 'Claims submitted'),
                value: person.activity.claims,
                muted: person.activity.claims === 0,
              },
              {
                key: 'last-registration',
                label: t('network.person.lastRegistration', 'Last registration'),
                value: person.activity.last_registration_at
                  ? formatDate(person.activity.last_registration_at)
                  : '-',
                muted: !person.activity.last_registration_at,
              },
            ]}
          />
        </section>
      </main>
    </AppShell>
  )
}
